import React, { useEffect, useState, useMemo } from "react";
import { FileText, Search, Download, Eye, Filter, RefreshCw, Calendar, Hash, ChevronLeft, ChevronRight } from "lucide-react";
import { MolecularBg } from "../components/MolecularBg";
import { useSEO } from "../lib/useSEO";
import api from "../lib/api";

const API_BASE = process.env.REACT_APP_BACKEND_URL;
const PAGE_SIZE = 12;

const pdfUrl = (id) => `${API_BASE}/api/coas/${id}/pdf`;
const fmt = (iso) => iso ? new Date(iso).toLocaleDateString("en-AU", { day: "numeric", month: "short", year: "numeric" }) : "—";

export const CoaLibraryPage = () => {
    useSEO({
        title: "COA Library — Carbon Labs Certificates of Analysis",
        description: "Browse the full library of manufacturer-supplied Certificates of Analysis for Carbon Labs research peptides. View or download batch-level COAs by product.",
    });

    const [coas, setCoas] = useState([]);
    const [products, setProducts] = useState([]);
    const [manufacturers, setManufacturers] = useState([]);
    const [search, setSearch] = useState("");
    const [productFilter, setProductFilter] = useState("");
    const [manuFilter, setManuFilter] = useState("");
    const [sort, setSort] = useState("newest");
    const [loading, setLoading] = useState(true);
    const [page, setPage] = useState(1);

    const load = async () => {
        setLoading(true);
        try {
            const [c, p, m] = await Promise.all([
                api.get("/coas", { params: { sort, ...(productFilter && { product_slug: productFilter }), ...(manuFilter && { manufacturer: manuFilter }), ...(search && { search }) } }),
                api.get("/products"),
                api.get("/coas/manufacturers"),
            ]);
            setCoas(c.data);
            setProducts(p.data);
            setManufacturers(m.data);
        } catch { /* ignore */ }
        setLoading(false);
    };

    useEffect(() => { load(); /* eslint-disable-next-line */ }, [productFilter, manuFilter, sort]);
    useEffect(() => {
        const id = setTimeout(load, 300);
        return () => clearTimeout(id);
        // eslint-disable-next-line
    }, [search]);

    useEffect(() => setPage(1), [search, productFilter, manuFilter, sort]);

    const pageCount = Math.max(1, Math.ceil(coas.length / PAGE_SIZE));
    const visible = useMemo(() => coas.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE), [coas, page]);

    return (
        <div className="pt-28 pb-20 min-h-screen relative" data-testid="coa-library-page">
            <MolecularBg intensity="low" />
            <div className="container-x relative z-10">
                <span className="eyebrow">// Compliance</span>
                <h1 className="font-display text-4xl md:text-5xl font-extrabold text-white tracking-tighter mt-2 italic mb-3">
                    COA <span className="text-[var(--gold)]">Library</span>
                </h1>
                <p className="text-sm text-[var(--text-secondary)] max-w-2xl mb-8">
                    Browse manufacturer-supplied Certificates of Analysis for every Carbon Labs product. Each COA represents the applicable manufacturing batch and is provided by the manufacturer.
                </p>

                {/* Filters */}
                <div className="flex flex-wrap gap-3 mb-6">
                    <div className="relative flex-1 min-w-[220px]">
                        <Search size={14} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-[var(--text-muted)]" />
                        <input
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            placeholder="Search product, batch, manufacturer…"
                            className="w-full pl-10 pr-4 py-2.5 bg-black/40 border border-white/10 rounded-full text-sm text-white placeholder:text-[var(--text-muted)] focus:outline-none focus:border-[var(--gold)]"
                            data-testid="coa-library-search"
                        />
                    </div>
                    <select
                        value={productFilter}
                        onChange={(e) => setProductFilter(e.target.value)}
                        className="px-4 py-2.5 bg-black/40 border border-white/10 rounded-full text-sm text-white focus:outline-none focus:border-[var(--gold)] min-w-[180px]"
                        data-testid="coa-library-product"
                    >
                        <option value="">All products</option>
                        {products.map((p) => (<option key={p.slug} value={p.slug}>{p.name}</option>))}
                    </select>
                    <select
                        value={manuFilter}
                        onChange={(e) => setManuFilter(e.target.value)}
                        className="px-4 py-2.5 bg-black/40 border border-white/10 rounded-full text-sm text-white focus:outline-none focus:border-[var(--gold)] min-w-[180px]"
                        data-testid="coa-library-manufacturer"
                    >
                        <option value="">All manufacturers</option>
                        {manufacturers.map((m) => (<option key={m} value={m}>{m}</option>))}
                    </select>
                    <select
                        value={sort}
                        onChange={(e) => setSort(e.target.value)}
                        className="px-4 py-2.5 bg-black/40 border border-white/10 rounded-full text-sm text-white focus:outline-none focus:border-[var(--gold)]"
                        data-testid="coa-library-sort"
                    >
                        <option value="newest">Newest first</option>
                        <option value="alpha">Alphabetical</option>
                    </select>
                    <button onClick={load} className="btn-neon" data-testid="coa-library-refresh">
                        <RefreshCw size={14} /> Refresh
                    </button>
                </div>

                {/* List */}
                {loading ? (
                    <div className="text-center py-16 text-[var(--text-secondary)]">Loading…</div>
                ) : visible.length === 0 ? (
                    <div className="glass rounded-2xl p-12 text-center" data-testid="coa-library-empty">
                        <FileText size={42} className="mx-auto text-[var(--text-muted)] mb-3" />
                        <p className="text-[var(--text-secondary)]">No COAs match these filters yet.</p>
                    </div>
                ) : (
                    <>
                        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                            {visible.map((c) => (
                                <article
                                    key={c.id}
                                    className="glass-elevated rounded-2xl p-5 flex flex-col"
                                    data-testid={`coa-library-card-${c.id}`}
                                >
                                    <div className="flex items-start gap-3">
                                        <div className="w-10 h-10 shrink-0 rounded-lg bg-emerald-500/10 border border-emerald-500/30 flex items-center justify-center">
                                            <FileText size={18} className="text-emerald-300" />
                                        </div>
                                        <div className="flex-1 min-w-0">
                                            <div className="font-display font-bold text-white text-base truncate">{c.product_name}</div>
                                            {c.is_current && (
                                                <span className="inline-flex items-center text-[9px] font-mono uppercase tracking-widest px-1.5 py-0.5 mt-1 rounded-full border border-[var(--gold)]/40 text-[var(--gold)]">Current</span>
                                            )}
                                        </div>
                                    </div>
                                    <div className="mt-3 space-y-1 text-xs text-[var(--text-secondary)]">
                                        {c.batch_number && (
                                            <div className="flex items-center gap-1.5">
                                                <Hash size={11} className="text-[var(--gold)]" /> <span className="font-mono">{c.batch_number}</span>
                                            </div>
                                        )}
                                        {c.manufacturer && (
                                            <div className="flex items-center gap-1.5">
                                                <Filter size={11} className="text-[var(--gold)]" /> {c.manufacturer}
                                            </div>
                                        )}
                                        <div className="flex items-center gap-1.5">
                                            <Calendar size={11} className="text-[var(--gold)]" /> {fmt(c.upload_date)}
                                        </div>
                                    </div>
                                    <div className="mt-auto pt-4 flex gap-2">
                                        <a
                                            href={pdfUrl(c.id)}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="flex-1 text-center px-3 py-2 rounded-full border border-[var(--gold)]/40 text-[var(--gold)] hover:bg-[var(--gold)]/10 text-xs font-mono uppercase tracking-widest inline-flex items-center justify-center gap-1.5"
                                            data-testid={`coa-library-view-${c.id}`}
                                        >
                                            <Eye size={11} /> View
                                        </a>
                                        <a
                                            href={pdfUrl(c.id)}
                                            download={c.filename}
                                            className="flex-1 text-center px-3 py-2 rounded-full bg-[var(--gold)] text-black text-xs font-mono uppercase tracking-widest font-bold hover:bg-[#F5C932] inline-flex items-center justify-center gap-1.5"
                                            data-testid={`coa-library-download-${c.id}`}
                                        >
                                            <Download size={11} /> PDF
                                        </a>
                                    </div>
                                </article>
                            ))}
                        </div>

                        {pageCount > 1 && (
                            <div className="flex items-center justify-center gap-3 mt-8" data-testid="coa-library-pager">
                                <button
                                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                                    disabled={page === 1}
                                    className="p-2 rounded-full border border-white/10 text-white disabled:opacity-30"
                                    aria-label="Previous page"
                                >
                                    <ChevronLeft size={14} />
                                </button>
                                <span className="text-xs font-mono uppercase tracking-widest text-[var(--text-secondary)]">
                                    Page {page} of {pageCount}
                                </span>
                                <button
                                    onClick={() => setPage((p) => Math.min(pageCount, p + 1))}
                                    disabled={page === pageCount}
                                    className="p-2 rounded-full border border-white/10 text-white disabled:opacity-30"
                                    aria-label="Next page"
                                >
                                    <ChevronRight size={14} />
                                </button>
                            </div>
                        )}
                    </>
                )}
            </div>
        </div>
    );
};
